import { Ref } from "vue";
import { useGsap, CustomEaseNames, CustomEaseDurations } from "../gsap-register";

const isOpenClass = "is-open";
const { gsap } = useGsap();

export interface AccordionConfig {
    headerClassName?: string;
    bodyClassName?: string;
    openOnlyOne?: boolean;
    withScrollIntoView?: Ref<boolean>;
    noClickListeners?: boolean;
}

export function useAccordion({
    headerClassName = "ds-accordion-item__header",
    bodyClassName = "ds-accordion-item__body",
    openOnlyOne = true,
    withScrollIntoView,
    noClickListeners = false,
}: AccordionConfig = {}) {
    // https://codepen.io/dusanralic/pen/PoqGKLM
    const headerSelector = `.${headerClassName}`;
    const bodySelector = `.${bodyClassName}`;
    const accordionElements = [];
    let destroyController: AbortController = null;

    const openAccordion = async (accordionElement: Element, withAnimation = true) => {
        accordionElement.classList.add(isOpenClass);
        accordionElement.querySelector<HTMLButtonElement>(headerSelector).setAttribute("aria-expanded", "true");

        if (withScrollIntoView?.value) {
            accordionElement.scrollIntoView({ behavior: "smooth" });
        }

        const bodyElement = accordionElement.querySelector<HTMLElement>(bodySelector);
        if (withAnimation) {
            await gsap.fromTo(
                bodyElement,
                {
                    height: 0,
                },
                {
                    height: bodyElement.scrollHeight,
                    duration: CustomEaseDurations.MOTION_DURATION_250,
                    ease: CustomEaseNames.MOTION_EASE_IN_OUT,
                }
            );
        }

        gsap.set(bodyElement, { height: "auto" });
    };

    const closeAccordion = async (accordionElement: Element, withAnimation = true) => {
        accordionElement.classList.remove(isOpenClass);
        accordionElement.querySelector<HTMLButtonElement>(headerSelector).setAttribute("aria-expanded", "false");

        const bodyElement = accordionElement.querySelector<HTMLElement>(bodySelector);

        if (!bodyElement) return;

        if (withAnimation) {
            await gsap.to(bodyElement, {
                height: 0,
                duration: CustomEaseDurations.MOTION_DURATION_250,
                ease: CustomEaseNames.MOTION_EASE_IN_OUT,
            });
        }

        gsap.set(bodyElement, { height: null });
    };

    const onKeyDown = (event: KeyboardEvent, headerElement: HTMLElement, allHeaders: HTMLElement[]) => {
        const currentIndex = allHeaders.indexOf(headerElement);

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                const nextIndex = (currentIndex + 1) % allHeaders.length;
                allHeaders[nextIndex].focus();
                break;

            case "ArrowUp":
                event.preventDefault();
                const prevIndex = (currentIndex - 1 + allHeaders.length) % allHeaders.length;
                allHeaders[prevIndex].focus();
                break;

            case "Home":
                event.preventDefault();
                allHeaders[0].focus();
                break;

            case "End":
                event.preventDefault();
                allHeaders[allHeaders.length - 1].focus();
                break;

            case "Enter":
                event.preventDefault();
                headerElement.click(); // Trigger click handler to open/close accordion
                break;

            default:
                break;
        }
    };

    const isOpen = (accordionElement: Element) => accordionElement.classList.contains(isOpenClass);

    const toggleAccordion = (accordionItemElement: Element, withAnimation = true) => {
        if (isOpen(accordionItemElement)) {
            closeAccordion(accordionItemElement, withAnimation);
        } else {
            if (openOnlyOne) {
                accordionElements
                    .filter((accordion) => isOpen(accordion))
                    .forEach((accordion) => {
                        closeAccordion(accordion, withAnimation);
                    });
            }

            openAccordion(accordionItemElement, withAnimation);
        }
    };

    /**
     * Opens the accordion at the given index
     *
     * If the accordion is already open, nothing happens
     */
    const openAccordionAtIndex = (index: number, withAnimation = true) => {
        const accordionElement = accordionElements[index];
        if (isOpen(accordionElement)) return;
        toggleAccordion(accordionElement, withAnimation);
    };

    /**
     * Closes the accordion at the given index
     *
     * If the accordion is already closed, nothing happens
     */
    const closeAccordionAtIndex = (index: number, withAnimation = true) => {
        const accordionElement = accordionElements[index];
        if (!isOpen(accordionElement)) return;
        toggleAccordion(accordionElement, withAnimation);
    };

    /**
     * Toggles the accordion at the given index
     */
    const toggleAccordionAtIndex = (index: number, withAnimation = true) => {
        const accordionElement = accordionElements[index];
        toggleAccordion(accordionElement, withAnimation);
    };

    const initAccordionItemElement = (accordionItemElement: Element, allHeaders: HTMLElement[]) => {
        const headerElement = accordionItemElement.querySelector<HTMLElement>(headerSelector);
        allHeaders.forEach((h) => h.setAttribute("aria-expanded", "false"));

        if (isOpen(accordionItemElement)) {
            openAccordion(accordionItemElement, false);
        }

        if (!noClickListeners) {
            headerElement.addEventListener("click", () => toggleAccordion(accordionItemElement), {
                signal: destroyController.signal,
            });

            headerElement.addEventListener("keydown", (event) => onKeyDown(event, headerElement, allHeaders), {
                signal: destroyController.signal,
            });
        }
    };

    const destroy = () => {
        destroyController?.abort();

        accordionElements.length = 0;
    };

    /**
     * Initializes the accordion with the given list of accordion elements
     *
     * If the accordion has already been initialized, it will be destroyed and reinitialized
     *
     * @returns A function to destroy the accordion events
     */
    const init = (accordionElementList: NodeListOf<Element>) => {
        if (!accordionElementList.length) return;

        if (destroyController) {
            destroy();
        }

        destroyController = new AbortController();
        accordionElements.push(...accordionElementList);
        const allHeaders: HTMLElement[] = [
            ...accordionElements.map((accordionItemElement) => accordionItemElement.querySelector(headerSelector)),
        ];

        accordionElements.forEach((accordionItemElement) => {
            initAccordionItemElement(accordionItemElement, allHeaders);
        });

        return destroy;
    };

    return {
        init,
        openAccordionAtIndex,
        closeAccordionAtIndex,
        toggleAccordionAtIndex,
    };
}
